import React from "react";
import {
  OnboardingAnswerValue,
  OnboardingQuestion,
  SingleChoiceQuestion,
  MultiChoiceQuestion,
  SliderQuestion,
  TextQuestion,
  DatePickerQuestion,
  QrCodeQuestion,
  DynamicSelectQuestion,
} from "../types";
import {
  ensureSliderValue,
  toMultiChoiceAnswer,
  toSingleChoiceAnswer,
} from "../utils/answerHelpers";
import { SingleChoiceQuestionField } from "./questions/SingleChoiceQuestion";
import { MultiChoiceQuestionField } from "./questions/MultiChoiceQuestion";
import { SliderQuestionField } from "./questions/SliderQuestion";
import { TextQuestionField } from "./questions/TextQuestion";
import { DatePickerQuestionField } from "./questions/DatePickerQuestion";
import { QrCodeQuestionField } from "./questions/QrCodeQuestion";
import { DynamicSelectQuestionField } from "./questions/DynamicSelectQuestion";

interface QuestionRendererProps {
  question: OnboardingQuestion;
  value: OnboardingAnswerValue | undefined;
  onChange: (value: OnboardingAnswerValue) => void;
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  value,
  onChange,
}) => {
  switch (question.type) {
    case "single": {
      const parsed = toSingleChoiceAnswer(value);
      return (
        <SingleChoiceQuestionField
          question={question as SingleChoiceQuestion}
          value={parsed}
          onChange={(answer) => onChange(answer)}
        />
      );
    }
    case "multi": {
      const parsed = toMultiChoiceAnswer(value);
      return (
        <MultiChoiceQuestionField
          question={question as MultiChoiceQuestion}
          value={parsed}
          onChange={(answer) => onChange(answer)}
        />
      );
    }
    case "slider": {
      const sliderQuestion = question as SliderQuestion;
      const sliderValue = ensureSliderValue(sliderQuestion, value);
      return (
        <SliderQuestionField
          question={sliderQuestion}
          value={sliderValue}
          onChange={(updated) => onChange(updated)}
        />
      );
    }
    case "text": {
      const textValue = typeof value === "string" ? value : "";
      return (
        <TextQuestionField
          question={question as TextQuestion}
          value={textValue}
          onChange={(updated) => onChange(updated)}
        />
      );
    }
    case "datepicker": {
      const dateValue = typeof value === "string" ? value : "";
      return (
        <DatePickerQuestionField
          question={question as DatePickerQuestion}
          value={dateValue}
          onChange={(updated) => onChange(updated)}
        />
      );
    }
    case "qrcode": {
      const codeValue = typeof value === "string" ? value : "";
      return (
        <QrCodeQuestionField
          question={question as QrCodeQuestion}
          value={codeValue}
          onChange={(updated) => onChange(updated)}
        />
      );
    }
    case "dynamic_select": {
      const selectValue = typeof value === "string" ? value : "";
      return (
        <DynamicSelectQuestionField
          question={question as DynamicSelectQuestion}
          value={selectValue}
          onChange={(updated) => onChange(updated)}
        />
      );
    }
    default:
      return null;
  }
};
